'use client';

import { useMemo, useState } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import fuzzysort from 'fuzzysort';
import { FilterIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';

import { CreateEmployeeDialog } from '@/app/(app)/settings/employees/dialog';
import { EMPLOYEE_COLUMNS } from '@/app/(app)/settings/employees/table/columns';
import { Popover } from '@/components/atoms/popover';
import { Select } from '@/components/atoms/select';
import { DataTable } from '@/components/data-table';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Filters {
  search: string;
  status: ApiSchemas['EmployeeStatus'] | null;
  role: ApiSchemas['EmployeeRole'] | null;
  bookable: boolean | null;
}

export function EmployeesTable() {
  const { api } = useApiV1Ctx();
  const { data } = api.employees.getAllEmployeesGet.useQuery();
  const [filters, setFilters] = useState<Filters>({
    search: '',
    status: null,
    role: null,
    bookable: null,
  });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const selectedIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  const employees = useMemo(() => {
    const items = data?.items ?? [];
    return items.map((e) => ({
      ...e,
      _search: [e.first_name, e.last_name, e.email, e.phone_number].filter(Boolean).join(' '),
    }));
  }, [data?.items]);

  const filteredEmployees = useMemo(() => {
    let filtered = employees;

    if (filters.status) {
      filtered = filtered.filter((e) => e.status === filters.status);
    }
    if (filters.role) {
      filtered = filtered.filter((e) => e.role === filters.role);
    }
    if (filters.bookable !== null) {
      filtered = filtered.filter((e) => e.bookable === filters.bookable);
    }

    const query = filters.search?.trim().toLowerCase();
    if (!query) return filtered;

    const r = fuzzysort.go(query, filtered, {
      key: '_search',
      threshold: 0.3,
      limit: 100,
    });
    return r.map((x) => x.obj);
  }, [employees, filters]);

  const table = useReactTable({
    getRowId: (row) => row.id,
    data: filteredEmployees,
    columns: EMPLOYEE_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  });

  return (
    <div className='space-y-3'>
      <Header filters={filters} setFilters={setFilters} />
      <HeaderSelectedActions selectedIds={selectedIds} reset={() => setRowSelection({})} />
      <div className='overflow-hidden rounded-md border'>
        <DataTable table={table} columns={EMPLOYEE_COLUMNS} />
      </div>
    </div>
  );
}

interface HeaderProps {
  filters: Filters;
  setFilters: (v: Filters) => void;
}

function Header({ filters, setFilters }: HeaderProps) {
  const onChange = (v: Partial<Filters>) => {
    setFilters({ ...filters, ...v });
  };

  return (
    <div className='flex items-center justify-between gap-4'>
      <div className='flex w-full items-center gap-2'>
        <Popover
          trigger={
            <Button variant='outline' size='icon'>
              <FilterIcon className='size-4' />
            </Button>
          }
          className='w-[400px] space-y-4 p-4'
        >
          <div className='flex w-full items-center'>
            <Label className='w-1/2'>Status</Label>
            <Select
              options={[
                { label: 'Active', value: 'ACTIVE' },
                { label: 'Inactive', value: 'INACTIVE' },
                { label: 'All', value: 'ALL' },
              ]}
              value={filters.status ?? 'ALL'}
              onChange={(v) => {
                if (v === 'ALL') {
                  onChange({ status: null });
                } else {
                  onChange({ status: v });
                }
              }}
              className='w-full'
            />
          </div>
          <div className='flex w-full items-center'>
            <Label className='w-1/2'>Role</Label>
            <Select
              options={[
                { label: 'Admin', value: 'ADMIN' },
                { label: 'Manager', value: 'MANAGER' },
                { label: 'Technician', value: 'TECHNICIAN' },
                { label: 'Staff', value: 'STAFF' },
                { label: 'All', value: 'ALL' },
              ]}
              value={filters.role ?? 'ALL'}
              onChange={(v) => {
                if (v === 'ALL') {
                  onChange({ role: null });
                } else {
                  onChange({ role: v });
                }
              }}
              className='w-full'
            />
          </div>
          <div className='flex w-full items-center'>
            <Label className='w-1/2'>Bookable</Label>
            <Select
              options={[
                { label: 'Yes', value: 'YES' },
                { label: 'No', value: 'NO' },
                { label: 'All', value: 'ALL' },
              ]}
              value={filters.bookable === null ? 'ALL' : filters.bookable ? 'YES' : 'NO'}
              onChange={(v) => {
                if (v === 'ALL') {
                  onChange({ bookable: null });
                } else {
                  onChange({ bookable: v === 'YES' });
                }
              }}
              className='w-full'
            />
          </div>
        </Popover>
        <Input
          placeholder='Search employees...'
          value={filters.search}
          onChange={(e) => onChange({ search: e.target.value })}
          className='max-w-sm'
        />
      </div>
      <CreateEmployeeDialog />
    </div>
  );
}

interface HeaderSelectedActionsProps {
  selectedIds: string[];
  reset: () => void;
}

function HeaderSelectedActions({ selectedIds, reset }: HeaderSelectedActionsProps) {
  const { api } = useApiV1Ctx();
  const mutation = api.employees.batchUpdateEmployees.useMutation(undefined, {
    onSuccess: () => {
      reset();
      api.employees.getAllEmployeesGet.invalidateQueries();
      toast.success('Employees updated');
    },
  });

  if (selectedIds.length === 0) return null;

  return (
    <div className='flex items-center justify-between gap-2 rounded-lg bg-muted/20 p-3'>
      <div className='flex w-full items-center justify-between gap-2'>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='outline'>Update All</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className='w-48' align='start'>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Bookable Status</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => mutation.mutate({ body: { ids: selectedIds, bookable: true } })}>
                    Is Bookable
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => mutation.mutate({ body: { ids: selectedIds, bookable: false } })}>
                    Is Not Bookable
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className='flex h-9 items-center rounded-md border border-dashed bg-background shadow'>
          <div className='px-4 text-sm font-medium text-foreground/70'>{selectedIds.length} selected</div>
          <Button
            variant='none'
            size='icon'
            className='rounded-l-none border-l border-dashed hover:bg-accent'
            onClick={reset}
          >
            <XIcon className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  );
}
